import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/features", label: "Features" },
  { to: "/scanner", label: "Scanner" },
  { to: "/apk-checker", label: "APK Checker" },
  { to: "/security", label: "Security" },
  { to: "/blog", label: "Blog" },
  { to: "/report", label: "Report a Link" },
  { to: "/contact", label: "Contact" },
];

export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.emergent.linkshieldai.lmnv0s";

export const WebHeader = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header
      data-testid="web-header"
      className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-2.5 group">
          <img src="/brand-shield.png" alt="LinkExpln logo" className="h-9 w-9 object-contain transition-transform duration-300 group-hover:rotate-6" />
          <span className="font-heading font-black tracking-tight text-lg text-white">
            LINKEXPLN<span className="text-[#00C5FE] text-[10px] align-super">™</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-7 xl:ml-8 2xl:ml-16" aria-label="Main navigation">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link${item.to.replace(/\//g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap font-mono-ls text-[11px] tracking-[0.18em] uppercase transition-colors duration-200 ${
                  isActive ? "text-[#00C5FE]" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-cta-get-app"
            className="whitespace-nowrap inline-flex items-center gap-1.5 border border-[#00C5FE]/50 text-[#00C5FE] font-heading font-bold text-sm px-3 xl:px-4 2xl:px-5 py-2.5 hover:bg-[#00C5FE]/10 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
          >
            Get the App <ArrowUpRight size={15} strokeWidth={2.5} />
          </a>
          <Link
            to="/scanner"
            data-testid="header-cta-scan"
            className="whitespace-nowrap inline-flex items-center gap-1.5 btn-brand text-black font-heading font-bold text-sm px-3 xl:px-4 2xl:px-5 py-2.5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
          >
            Scan a Link <ArrowUpRight size={15} strokeWidth={2.5} />
          </Link>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-white p-2 focus:outline-none focus:ring-2 focus:ring-[#00C5FE]"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="xl:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
            aria-label="Mobile navigation"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link${item.to.replace(/\//g, "-")}`}
                  className={`font-mono-ls text-xs tracking-[0.18em] uppercase py-1 ${
                    pathname === item.to ? "text-[#00C5FE]" : "text-zinc-300"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-get-app"
                className="mt-2 inline-flex items-center justify-center gap-1.5 border border-[#00C5FE]/50 text-[#00C5FE] font-heading font-bold text-sm px-5 py-3"
              >
                Get the App <ArrowUpRight size={15} strokeWidth={2.5} />
              </a>
              <Link
                to="/scanner"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta-scan"
                className="mt-2 inline-flex items-center justify-center gap-1.5 bg-[#00C5FE] text-black font-heading font-bold text-sm px-5 py-3"
              >
                Scan a Link <ArrowUpRight size={15} strokeWidth={2.5} />
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
